import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import { getNewsById, getSortedNews } from "@/utils/comic";

export default function NewsDetailPage() {
  const { newsId } = useParams();
  const news = getNewsById(newsId);
  const [copied, setCopied] = useState(false);

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-16">
        <EmptyState
          icon="ri-article-line"
          title="资讯不存在或加载错误"
          description="这篇资讯可能已经被移除，返回资讯列表看看其他最新内容吧。"
          action={
            <Link
              to="/news"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
            >
              返回资讯列表
            </Link>
          }
        />
      </div>
    );
  }

  const related = getSortedNews()
    .filter((item) => item.id !== news.id)
    .slice(0, 4);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="w-full">
      <article className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "动漫资讯", to: "/news" },
            { label: news.category },
          ]}
        />

        <div className="mt-6 flex flex-col lg:flex-row gap-10">
          <div className="lg:flex-1 min-w-0">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label font-bold">
              <i className="ri-price-tag-3-line"></i>
              {news.category}
            </span>
            <h1 className="mt-4 text-2xl md:text-4xl font-label font-bold text-foreground-950 leading-snug">
              {news.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
              <span>
                <i className="ri-calendar-line mr-1"></i>
                {news.date}
              </span>
              <span>
                <i className="ri-newspaper-line mr-1"></i>
                来源：{news.source}
              </span>
              <span>
                <i className="ri-eye-line mr-1"></i>
                {news.views} 阅读
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 font-label font-bold text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                <i className={copied ? "ri-check-line" : "ri-link"}></i>
                {copied ? "已复制" : "复制链接"}
              </button>
            </div>

            <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
              <img
                src={news.cover}
                alt={news.title}
                title={`${news.title} - 蛙漫动漫资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {news.content.map((block, index) => {
                if (block.type === "heading") {
                  return (
                    <h2
                      key={index}
                      className="text-lg md:text-xl font-label font-bold text-foreground-950 mt-2"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "image") {
                  return (
                    <div
                      key={index}
                      className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100"
                    >
                      <img
                        src={block.url}
                        alt={`${news.title} 配图`}
                        title={`${news.title} - 蛙漫动漫资讯`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  );
                }
                return (
                  <p
                    key={index}
                    className="text-sm md:text-base leading-loose text-foreground-700"
                  >
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-300 text-foreground-800 text-sm font-label font-bold hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯列表
              </Link>
            </div>
          </div>

          <aside className="w-full lg:w-[320px] shrink-0">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
              <h2 className="text-base font-label font-bold text-foreground-950 mb-2">
                相关推荐
              </h2>
              {related.map((item) => (
                <NewsCard key={item.id} news={item} />
              ))}
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}